import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface Competency {
  id: number;
  name: string;
  weight: string;
}

interface CompetencyForm {
  name: string;
  weight: string;
}

const emptyForm: CompetencyForm = { name: '', weight: '' };

export function Competencies() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<Competency[] | null>(null);
  const [editing, setEditing] = useState<'new' | Competency | null>(null);
  const [form, setForm] = useState<CompetencyForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<Competency[]>('/competencies').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: Competency) => {
    setForm({ name: item.name, weight: item.weight });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof CompetencyForm) =>
    (e: ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/competencies', form);
      else if (editing) await api.put(`/competencies/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: Competency) => {
    if (!(await confirmDelete(`Delete the competency "${item.name}"?`))) return;
    try {
      await api.delete(`/competencies/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Competencies</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <p className="muted">
        Organization-wide behavioral competencies (Communication, Integrity, Teamwork…) scored on every review alongside,
        but never blended into, the KPI/KRA-based score.
      </p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Competency</th>
                <th>Weight</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="muted">{item.weight}%</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No competencies configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add competency' : 'Edit competency'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Weight (%) *
              <input type="number" step="0.1" min="0" max="100" value={form.weight} onChange={set('weight')} required />
              {errors.weight && <small className="error">{errors.weight[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
